import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { date } from '../../lib/format';
import { Button, Card, CardHeader } from '../../components/ui';

/** One counter's record of a number another customer already owns. */
export interface PhoneMatch {
  heldId: string;
  heldName: string;
  phone: string;
  branchNames: string[];
  heldCreatedAt: string;
  matchId: string;
  matchName: string;
}

/**
 * Numbers a counter typed that already belong to a customer it cannot see.
 *
 * The counter was not told — telling it would have revealed who the business
 * knows — so the question lands here, with somebody who sees every location.
 * Only they can open this list; for anybody else the API refuses and the
 * panel stays out of the way.
 */
export function PhoneMatches(): ReactNode {
  const { business, can } = useAuth();
  const path = useBusinessPath();
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['customer-phone-matches', business?.businessId],
    queryFn: async () => {
      try {
        return await api.get<PhoneMatch[]>(path('/customers/phone-matches'));
      } catch (error) {
        // Confined to locations: not theirs to decide, and not an error.
        if (error instanceof ApiError && error.status === 403) return [];
        throw error;
      }
    },
    enabled: Boolean(business) && can('customers:update'),
  });

  const decide = useMutation({
    mutationFn: ({ heldId, same }: { heldId: string; same: boolean }) =>
      api.post(path(`/customers/${heldId}/${same ? 'confirm-match' : 'keep-separate'}`), {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customer-phone-matches'] });
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  if (!data || data.length === 0) return null;
  const timezone = business?.timezone ?? 'UTC';

  return (
    <Card className="mb-4">
      <CardHeader
        title="Numbers to check"
        subtitle="A counter recorded a phone number that already belongs to a customer it cannot see. Is it the same person?"
      />
      <ul className="divide-y divide-slate-100">
        {data.map((m) => (
          <li key={m.heldId} className="flex flex-wrap items-center justify-between gap-3 py-3">
            <div className="text-sm">
              <p className="font-medium text-slate-900">{m.phone}</p>
              <p className="text-slate-600">
                “{m.heldName}” at {m.branchNames.join(', ') || 'no location'}, {date(m.heldCreatedAt, timezone)} —
                already “{m.matchName}”
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={decide.isPending}
                onClick={() => decide.mutate({ heldId: m.heldId, same: true })}
              >
                Same person
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={decide.isPending}
                onClick={() => decide.mutate({ heldId: m.heldId, same: false })}
              >
                Different people
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
